// src/components/ui/MultiSelectDropdown.jsx
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom'; // Import createPortal
import Button from './Button';

// Icons remain the same...
const ChevronDownIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
);
const CrossIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
);

function MultiSelectDropdown({ options, selectedOptions, onChange, placeholder = 'Select Options', ...buttonProps }) {
    const [isOpen, setIsOpen] = useState(false);
    const [panelStyle, setPanelStyle] = useState({}); // State for dynamic style
    const dropdownRef = useRef(null); // Ref for the entire component container
    const buttonRef = useRef(null); // Ref for the button to measure position

    const safeOptions = Array.isArray(options) ? options : [];
    const safeSelectedOptions = Array.isArray(selectedOptions) ? selectedOptions : [];

    // Calculate panel position when opening
    useEffect(() => {
        if (isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            const panelHeightEstimate = 240; // Corresponds to max-h-60 (60 * 4px) - adjust if needed
            const margin = 4; // Roughly mt-1 / mb-1

            let top;
            let bottom = 'auto';

            // Prefer position below, but flip if not enough space and more space above
            if (spaceBelow >= panelHeightEstimate || spaceBelow >= spaceAbove) {
                top = rect.bottom + window.scrollY + margin;
            } else {
                // Position above
                // Calculate bottom relative to viewport top + scroll offset
                bottom = (window.innerHeight - rect.top - window.scrollY + margin);
                top = 'auto'; // Use bottom positioning instead of top
            }

            setPanelStyle({
                position: 'absolute', // Position relative to document body now
                top: top !== 'auto' ? `${top}px` : 'auto',
                bottom: bottom !== 'auto' ? `${bottom}px` : 'auto', // Use bottom positioning when opening upwards
                left: `${rect.left + window.scrollX}px`,
                width: `${rect.width}px`, // Match button width
                zIndex: 1100, // Ensure it's above modals (common modal z-index is 1050)
            });
        }
    }, [isOpen]); // Recalculate when isOpen changes

    // Close dropdown when clicking outside (needs adjustment for portal)
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if click is outside the main component AND the portal panel (if it exists)
            const panelElement = document.getElementById('multiselect-panel'); // Need an ID on the panel
            if (
                dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                (!panelElement || !panelElement.contains(event.target))
            ) {
                setIsOpen(false);
            }
        };
        // Only add listener if dropdown is open
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        // Cleanup
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, dropdownRef]); // Add isOpen dependency

    const handleOptionToggle = (option) => {
        if (typeof onChange !== 'function') { return; }
        const newSelectedOptions = safeSelectedOptions.includes(option)
            ? safeSelectedOptions.filter(item => item !== option)
            : [...safeSelectedOptions, option];
        onChange(newSelectedOptions);
        // Optional: Keep dropdown open after selection? Default is it stays open.
        // setIsOpen(false);
    };

    const handleClearSelection = (e) => {
        e.stopPropagation();
        if (typeof onChange === 'function') {
            onChange([]);
        }
        setIsOpen(false);
    };

    const getButtonText = () => {
        if (safeSelectedOptions.length === 0) { return placeholder; }
        if (safeSelectedOptions.length === 1) { return safeSelectedOptions[0] || placeholder; }
        return `${safeSelectedOptions.length} selected`;
    };

    const displayOptions = safeOptions.filter(option => option != null && option !== '');

    // Panel JSX to be portaled
    const DropdownPanel = isOpen ? (
        <div
            id="multiselect-panel" // Added ID for click-outside detection
            style={panelStyle} // Apply calculated styles
            // Removed absolute/relative positioning classes, use base styles
            // Keep max-height and overflow
            className="bg-white shadow-xl rounded-xl border border-gray-200 max-h-60 overflow-y-auto"
        >
            <ul className="py-1">
                {displayOptions.length > 0 ? (
                    displayOptions.map(option => (
                        <li
                            key={option}
                            className="px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer flex items-center"
                            // Use onMouseDown to prevent click-outside listener from firing immediately
                            onMouseDown={(e) => {
                                e.preventDefault(); // Prevent blur/focus issues
                                handleOptionToggle(option);
                            }}
                            role="option"
                            aria-selected={safeSelectedOptions.includes(option)}
                        >
                            <input
                                type="checkbox"
                                checked={safeSelectedOptions.includes(option)}
                                readOnly
                                className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer pointer-events-none"
                                tabIndex="-1"
                            />
                            <span>{option}</span>
                        </li>
                    ))
                ) : (
                    <li className="px-3 py-2 text-sm text-gray-500 italic">No options available</li>
                )}
            </ul>
        </div>
    ) : null;


    return (
        // Container needs the main ref for click-outside detection
        <div className="inline-block w-full flex items-center gap-1" ref={dropdownRef}>
            {/* Button container needs the ref for position measurement */}
            <div className="flex-grow relative" ref={buttonRef}>
                <Button
                    type="button"
                    variant="secondary"
                    size="medium"
                    onClick={(e) => {
                        e.preventDefault();
                        setIsOpen(!isOpen);
                    }}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    className="w-full flex items-center justify-between gap-2 text-left px-4 py-3 rounded-xl border border-gray-200 bg-white/95 shadow-sm text-sm text-gray-700 min-h-[48px] transition hover:border-gray-300 hover:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                    {...buttonProps}
                >
                    <span className="flex-grow truncate font-normal">{getButtonText()}</span>
                    <span className="text-gray-400"><ChevronDownIcon /></span>
                </Button>
                {/* Dropdown Panel is rendered via Portal below */}
            </div>

            {/* Clear Button */}
            {safeSelectedOptions.length > 0 && (
                <button
                    type="button"
                    onClick={handleClearSelection}
                    className="p-2 text-gray-500 hover:text-red-600 focus:outline-none focus:ring-1 focus:ring-red-500 rounded-md flex-shrink-0"
                    title="Clear selection"
                    aria-label="Clear selection"
                >
                    <CrossIcon />
                </button>
            )}

            {/* Render the panel using a Portal */}
            {DropdownPanel && createPortal(DropdownPanel, document.body)}
        </div>
    );
}

export default MultiSelectDropdown;